import React, { useState, useEffect, useMemo, useRef } from "react";
import { placementCellApi } from "../../config/api";
import { apiClient } from "../../utils/apiClient";
import "./PlacementCell.scss";

// ─── Constants ────────────────────────────────────────────────────────────────
const MEMBER_ROLES = [
  'Placement Officer', 
  'Training & Placement Coordinator', 
  'Industry Relations Manager', 
  'Career Counsellor', 
  'Placement Head'
];

const COURSES = [
  'B.A.', 'B.Sc.', 'B.Com.', 'B.B.A.', 'B.C.A.', 'B.Tech.', 
  'M.A.', 'M.Sc.', 'M.Com.', 'M.B.A.', 'M.C.A.', 'M.Tech.', 'Ph.D.', 
  'All Courses'
];

const DRIVE_TYPES = [
  'Campus Drive', 'Off-Campus Drive', 'Pool Campus', 
  'Virtual Drive', 'Walk-in Drive'
];

const DRIVE_STATUSES = ['Upcoming', 'Completed', 'Cancelled', 'Postponed'];

const STATUS_META = {
  Upcoming:  { label: "Upcoming",  color: "#3b82f6", bg: "#eff6ff", border: "#bfdbfe" },
  Completed: { label: "Completed", color: "#22c55e", bg: "#f0fdf4", border: "#bbf7d0" },
  Cancelled: { label: "Cancelled", color: "#ef4444", bg: "#fef2f2", border: "#fecaca" },
  Postponed: { label: "Postponed", color: "#f59e0b", bg: "#fffbeb", border: "#fde68a" },
};

const BLANK_MEMBER = { 
  _id: null, Name: "", Role: "Placement Officer", Email: "", 
  Phone: "", Course: "All Courses", Office_Room: "", 
  About: "", Experience_Years: 0, Status: "active" 
};

const BLANK_COMPANY = { 
  _id: null, Company_Name: "", Drive_Type: "Campus Drive", 
  HR_Name: "", Job_Role: "", Package: "", Course: "All Courses", 
  Drive_Date: new Date().toISOString().slice(0, 10), 
  Venue: "", Remarks: "", Status: "Upcoming" 
};

const PlacementCell = () => {
  const [members, setMembers]               = useState([]);
  const [companies, setCompanies]           = useState([]);
  const [loading, setLoading]               = useState(false);
  const [activeTab, setActiveTab]           = useState("members");
  const [memberModal, setMemberModal]       = useState("");
  const [companyModal, setCompanyModal]     = useState("");
  const [memberForm, setMemberForm]         = useState(BLANK_MEMBER);
  const [companyForm, setCompanyForm]       = useState(BLANK_COMPANY);
  const [confirmState, setConfirmState]     = useState({ type: "", id: null });
  const [searchText, setSearchText]         = useState("");
  const [filterCourse, setFilterCourse]     = useState("all");
  const [dropdownOpenId, setDropdownOpenId] = useState(null);
  const [dropdownPos, setDropdownPos]       = useState({ top: 0, left: 0 });
  const dropdownRef = useRef(null);

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [memRes, comRes] = await Promise.all([
        apiClient(`${placementCellApi}/members`),
        apiClient(`${placementCellApi}/companies`)
      ]);
      if (memRes.ok) {
        const memResult = await memRes.json();
        setMembers(memResult.data || []);
      }
      if (comRes.ok) {
        const comResult = await comRes.json();
        setCompanies(comResult.data || []);
      }
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const onOut = (e) => { if (dropdownRef.current && !dropdownRef.current.contains(e.target)) setDropdownOpenId(null); };
    const onClose = () => setDropdownOpenId(null);
    document.addEventListener("mousedown", onOut);
    window.addEventListener("scroll", onClose, true);
    window.addEventListener("resize", onClose);
    return () => {
      document.removeEventListener("mousedown", onOut);
      window.removeEventListener("scroll", onClose, true);
      window.removeEventListener("resize", onClose);
    };
  }, []);

  // ── Derived ────────────────────────────────────────────────────────────────
  const filteredMembers = useMemo(() => members.filter(m => {
    const q = searchText.toLowerCase();
    const matchSearch = (m.Name || "").toLowerCase().includes(q) || (m.Role || "").toLowerCase().includes(q);
    const matchCourse = filterCourse === "all" || m.Course === filterCourse;
    return matchSearch && matchCourse;
  }), [members, searchText, filterCourse]);

  const filteredDrives = useMemo(() => companies.filter(c => {
    const q = searchText.toLowerCase();
    const matchSearch = (c.Company_Name || "").toLowerCase().includes(q) || (c.Job_Role || "").toLowerCase().includes(q);
    const matchCourse = filterCourse === "all" || c.Course === filterCourse;
    return matchSearch && matchCourse;
  }), [companies, searchText, filterCourse]);

  const stats = useMemo(() => ({
    totalMembers: members.length,
    activeMembers: members.filter(m => m.Status === 'active').length,
    totalDrives: companies.length,
    upcomingDrives: companies.filter(c => c.Status === 'Upcoming').length,
  }), [members, companies]);

  // ── Handlers ───────────────────────────────────────────────────────────────
  const handleDotsClick = (e, id) => {
    e.stopPropagation();
    if (dropdownOpenId === id) { setDropdownOpenId(null); return; }
    const rect = e.currentTarget.getBoundingClientRect();
    setDropdownPos({ top: rect.bottom + 8, left: rect.right - 190 });
    setDropdownOpenId(id);
  };

  const saveMember = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      const method = memberModal === "add" ? "POST" : "PUT";
      const url = memberModal === "add" ? `${placementCellApi}/members` : `${placementCellApi}/members/${memberForm._id}`;
      const response = await apiClient(url, { method, body: JSON.stringify(memberForm) });
      if (response.ok) { fetchData(); setMemberModal(""); }
    } catch (error) {
      console.error("Error saving member:", error);
    } finally {
      setLoading(false);
    }
  };

  const saveCompany = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      const method = companyModal === "add" ? "POST" : "PUT";
      const url = companyModal === "add" ? `${placementCellApi}/companies` : `${placementCellApi}/companies/${companyForm._id}`;
      const response = await apiClient(url, { method, body: JSON.stringify(companyForm) });
      if (response.ok) { fetchData(); setCompanyModal(""); }
    } catch (error) {
      console.error("Error saving company:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    try {
      setLoading(true);
      const type = confirmState.type === "member" ? "members" : "companies";
      await apiClient(`${placementCellApi}/${type}/${confirmState.id}`, { method: "DELETE" });
      fetchData();
      setConfirmState({ type: "", id: null });
    } catch (error) {
      console.error("Error deleting:", error);
    } finally {
      setLoading(false);
    }
  };

  const clearFilters = () => { setSearchText(""); setFilterCourse("all"); };

  return (
    <div className="pc-page">
      {/* ── Header ── */}
      <div className="pc-header">
        <div className="pc-header-left">
          <div className="pc-header-icon">💼</div>
          <div>
            <div className="pc-breadcrumb">
              <span>Facilities</span>
              <svg viewBox="0 0 24 24" width="13" height="13" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="9 18 15 12 9 6" /></svg>
              <span className="pc-bc-active">Placement Cell</span>
            </div>
            <h1>Placement Cell</h1>
            <p>Manage placement committee members and monitor upcoming campus recruitment drives and historical placement data.</p>
          </div>
        </div>
        <div className="pc-header-actions">
          <div className="pc-tabs">
            <button className={activeTab === "members" ? "active" : ""} onClick={() => setActiveTab("members")}>Committee Members</button>
            <button className={activeTab === "companies" ? "active" : ""} onClick={() => setActiveTab("companies")}>Campus Drives</button>
          </div>
          <button type="button" className="pc-add-btn" onClick={() => activeTab === "members" ? (setMemberForm(BLANK_MEMBER), setMemberModal("add")) : (setCompanyForm(BLANK_COMPANY), setCompanyModal("add"))}>
            + Add {activeTab === "members" ? "Member" : "Drive"}
          </button>
        </div>
      </div>

      {/* ── Stats ── */}
      <div className="pc-stats">
        <div className="pc-stat-card"><span>👥</span><div><strong>{stats.totalMembers}</strong><p>Total Committee</p></div></div>
        <div className="pc-stat-card"><span>✅</span><div><strong>{stats.activeMembers}</strong><p>Active Members</p></div></div>
        <div className="pc-stat-card"><span>🏢</span><div><strong>{stats.totalDrives}</strong><p>Total Drives</p></div></div>
        <div className="pc-stat-card"><span>🚀</span><div><strong>{stats.upcomingDrives}</strong><p>Upcoming Drives</p></div></div>
      </div>

      {/* ── Toolbar ── */}
      <div className="pc-toolbar">
        <input type="text" placeholder={`Search ${activeTab === 'members' ? 'members by name or role' : 'drives by company or role'}...`} value={searchText} onChange={(e) => setSearchText(e.target.value)} />
        <select value={filterCourse} onChange={(e) => setFilterCourse(e.target.value)}>
          <option value="all">All Eligible Courses</option>
          {COURSES.map(c => <option key={c} value={c}>{c}</option>)}
        </select>
        {(searchText || filterCourse !== 'all') && <button className="btn-light" onClick={clearFilters}>Clear</button>}
      </div>

      {loading && <div className="loading-overlay">Loading placement data...</div>}

      {/* ── Content ── */}
      <div className="pc-content">
        {activeTab === "members" ? (
          <div className="pc-grid">
            {filteredMembers.map(m => (
              <div key={m._id} className="pc-card">
                <div className="pc-card-head">
                  <div className="pc-avatar">{m.Name[0]}</div>
                  <div className="pc-meta"><h3>{m.Name}</h3><p>{m.Role}</p></div>
                  <button className="pc-dots" onClick={(e) => handleDotsClick(e, m._id)}>⋮</button>
                </div>
                <div className="pc-info-row">
                  <div><strong>Email:</strong> {m.Email}</div>
                  <div><strong>Phone:</strong> {m.Phone}</div>
                  <div><strong>Office:</strong> {m.Office_Room || 'TBD'}</div>
                </div>
                <div className="pc-badges"><span>{m.Course}</span><span>{m.Experience_Years}Y Experience</span></div>
              </div>
            ))}
            {filteredMembers.length === 0 && <div className="empty-msg">No committee members found matching your criteria.</div>}
          </div>
        ) : (
          <div className="pc-table-container">
            <div className="companies-table-wrap">
              <table className="pc-table">
                <thead><tr><th>Company & Role</th><th>Type</th><th>Eligible Course</th><th>Date & Venue</th><th>Package</th><th>Status</th><th style={{ width: 60 }}></th></tr></thead>
                <tbody>
                  {filteredDrives.map(c => {
                    const st = STATUS_META[c.Status] || STATUS_META.Upcoming;
                    return (
                      <tr key={c._id}>
                        <td>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                            <div className="pc-avatar company">{c.Company_Name[0]}</div>
                            <div><strong>{c.Company_Name}</strong><br/><small style={{ color: '#64748b', fontWeight: 600 }}>{c.Job_Role}</small></div>
                          </div>
                        </td>
                        <td><span className="type-badge">{c.Drive_Type}</span></td>
                        <td>{c.Course}</td>
                        <td><strong>{c.Drive_Date ? new Date(c.Drive_Date).toLocaleDateString() : 'TBD'}</strong><br/><small>{c.Venue || 'TBD'}</small></td>
                        <td><strong style={{ color: '#059669' }}>{c.Package}</strong></td>
                        <td><span className="status-badge" style={{ background: st.bg, color: st.color, borderColor: st.border }}>{st.label}</span></td>
                        <td><button className="pc-dots" onClick={(e) => handleDotsClick(e, c._id)}>⋮</button></td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            {filteredDrives.length === 0 && <div className="empty-msg">No campus drives found matching your criteria.</div>}
          </div>
        )}
      </div>

      {/* ── Dropdown ── */}
      {dropdownOpenId && (
        <div ref={dropdownRef} className="pc-dropdown" style={{ top: dropdownPos.top, left: dropdownPos.left }}>
          <button onClick={() => {
            if (activeTab === 'members') { setMemberForm(members.find(m => m._id === dropdownOpenId)); setMemberModal("edit"); }
            else { setCompanyForm(companies.find(c => c._id === dropdownOpenId)); setCompanyModal("edit"); }
            setDropdownOpenId(null);
          }}>Edit Details</button>
          <button style={{ color: '#ef4444' }} onClick={() => { setConfirmState({ type: activeTab === 'members' ? 'member' : 'company', id: dropdownOpenId }); setDropdownOpenId(null); }}>Delete Record</button>
        </div>
      )}

      {/* ── Modals ── */}
      {(memberModal || companyModal) && (
        <div className="pc-modal-overlay" onClick={() => { setMemberModal(""); setCompanyModal(""); }}>
          <div className="pc-modal form" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header" style={{ border: 'none', padding: 0, marginBottom: 20 }}>
              <div className="modal-header-left">
                <div className="pc-avatar" style={{ background: memberModal ? '#7c3aed' : '#0ea5e9' }}>{memberModal ? "👤" : "🏢"}</div>
                <div>
                  <h2>{memberModal ? (memberModal === "add" ? "Add Committee Member" : "Edit Member Details") : (companyModal === "add" ? "Register Campus Drive" : "Update Drive Information")}</h2>
                  <p>Enter the professional details for the {memberModal ? 'placement officer' : 'campus recruitment drive'} below.</p>
                </div>
              </div>
              <button type="button" className="modal-close" onClick={() => { setMemberModal(""); setCompanyModal(""); }}>×</button>
            </div>
            <form className="pc-form" onSubmit={memberModal ? saveMember : saveCompany}>
              {memberModal ? (
                <>
                  <div className="form-group"><label>Full Name</label><input value={memberForm.Name} onChange={(e) => setMemberForm({ ...memberForm, Name: e.target.value })} required /></div>
                  <div className="form-group"><label>Role</label><select value={memberForm.Role} onChange={(e) => setMemberForm({ ...memberForm, Role: e.target.value })}>{MEMBER_ROLES.map(r => <option key={r} value={r}>{r}</option>)}</select></div>
                  <div className="form-group"><label>Email Address</label><input type="email" value={memberForm.Email} onChange={(e) => setMemberForm({ ...memberForm, Email: e.target.value })} required /></div>
                  <div className="form-group"><label>Phone Number</label><input value={memberForm.Phone} onChange={(e) => setMemberForm({ ...memberForm, Phone: e.target.value })} /></div>
                  <div className="form-group"><label>Target Course</label><select value={memberForm.Course} onChange={(e) => setMemberForm({ ...memberForm, Course: e.target.value })}>{COURSES.map(c => <option key={c} value={c}>{c}</option>)}</select></div>
                  <div className="form-group"><label>Experience (Years)</label><input type="number" value={memberForm.Experience_Years} onChange={(e) => setMemberForm({ ...memberForm, Experience_Years: parseInt(e.target.value) })} /></div>
                  <div className="form-group"><label>Office Room</label><input value={memberForm.Office_Room} onChange={(e) => setMemberForm({ ...memberForm, Office_Room: e.target.value })} /></div>
                  <div className="form-group"><label>Status</label><select value={memberForm.Status} onChange={(e) => setMemberForm({ ...memberForm, Status: e.target.value })}><option value="active">Active</option><option value="inactive">Inactive</option></select></div>
                  <textarea placeholder="Brief profile or bio..." value={memberForm.About} onChange={(e) => setMemberForm({ ...memberForm, About: e.target.value })} />
                </>
              ) : (
                <>
                  <div className="form-group"><label>Company Name</label><input value={companyForm.Company_Name} onChange={(e) => setCompanyForm({ ...companyForm, Company_Name: e.target.value })} required /></div>
                  <div className="form-group"><label>Job Role</label><input value={companyForm.Job_Role} onChange={(e) => setCompanyForm({ ...companyForm, Job_Role: e.target.value })} /></div>
                  <div className="form-group"><label>HR Contact Name</label><input value={companyForm.HR_Name} onChange={(e) => setCompanyForm({ ...companyForm, HR_Name: e.target.value })} /></div>
                  <div className="form-group"><label>Drive Date</label><input type="date" value={companyForm.Drive_Date?.slice(0, 10)} onChange={(e) => setCompanyForm({ ...companyForm, Drive_Date: e.target.value })} required /></div>
                  <div className="form-group"><label>Drive Type</label><select value={companyForm.Drive_Type} onChange={(e) => setCompanyForm({ ...companyForm, Drive_Type: e.target.value })}>{DRIVE_TYPES.map(t => <option key={t} value={t}>{t}</option>)}</select></div>
                  <div className="form-group"><label>Package Details</label><input value={companyForm.Package} onChange={(e) => setCompanyForm({ ...companyForm, Package: e.target.value })} placeholder="e.g. 5.5 LPA" /></div>
                  <div className="form-group"><label>Eligible Course</label><select value={companyForm.Course} onChange={(e) => setCompanyForm({ ...companyForm, Course: e.target.value })}>{COURSES.map(c => <option key={c} value={c}>{c}</option>)}</select></div>
                  <div className="form-group"><label>Drive Status</label><select value={companyForm.Status} onChange={(e) => setCompanyForm({ ...companyForm, Status: e.target.value })}>{DRIVE_STATUSES.map(s => <option key={s} value={s}>{s}</option>)}</select></div>
                  <div className="form-group" style={{ gridColumn: '1 / -1' }}><label>Venue Address</label><input value={companyForm.Venue} onChange={(e) => setCompanyForm({ ...companyForm, Venue: e.target.value })} /></div>
                  <textarea placeholder="Additional instructions or remarks..." value={companyForm.Remarks} onChange={(e) => setCompanyForm({ ...companyForm, Remarks: e.target.value })} />
                </>
              )}
              <button type="submit">Save {memberModal ? 'Member Profile' : 'Drive Details'}</button>
            </form>
          </div>
        </div>
      )}

      {/* ── Confirm Delete ── */}
      {confirmState.id && (
        <div className="pc-modal-overlay" onClick={() => setConfirmState({ type: "", id: null })}>
          <div className="pc-modal" onClick={(e) => e.stopPropagation()}>
            <h2>Confirm Deletion</h2>
            <p>Are you sure you want to permanently remove this {confirmState.type === 'member' ? 'committee member' : 'campus drive'}? This action cannot be undone.</p>
            <div className="pc-confirm-actions">
              <button onClick={() => setConfirmState({ type: "", id: null })}>Cancel</button>
              <button onClick={handleDelete}>Yes, Delete Permanently</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PlacementCell;